import type { SARadio } from '@/mocks/superAdminRadios';

interface RadiosTableProps {
  radios: SARadio[];
  onView: (radio: SARadio) => void;
  loading?: boolean;
  error?: string;
  onRetry?: () => void;
  hasFilters?: boolean;
}

const planLabels: Record<string, { label: string; color: string; bg: string }> = {
  free: { label: 'Free', color: 'text-secondary-600', bg: 'bg-secondary-100' },
  pro: { label: 'Pro', color: 'text-primary-600', bg: 'bg-primary-100' },
  premium: { label: 'Premium', color: 'text-accent-600', bg: 'bg-accent-100' },
};

const statusStyle: Record<string, { label: string; dot: string; text: string; bg: string }> = {
  activa: { label: 'Activa', dot: 'bg-emerald-500', text: 'text-emerald-600', bg: 'bg-emerald-50' },
  solicitud_recibida: { label: 'Solicitud', dot: 'bg-sky-400', text: 'text-sky-600', bg: 'bg-sky-50' },
  en_revision: { label: 'En Revisión', dot: 'bg-amber-400', text: 'text-amber-600', bg: 'bg-amber-50' },
  suspendida: { label: 'Suspendida', dot: 'bg-red-500', text: 'text-red-500', bg: 'bg-red-50' },
  cancelada: { label: 'Cancelada', dot: 'bg-stone-500', text: 'text-stone-500', bg: 'bg-stone-100' },
  archivada: { label: 'Archivada', dot: 'bg-stone-400', text: 'text-stone-400', bg: 'bg-stone-50' },
  pendiente_contrato: { label: 'Pend. Contrato', dot: 'bg-violet-400', text: 'text-violet-600', bg: 'bg-violet-50' },
  pendiente_firma: { label: 'Pend. Firma', dot: 'bg-indigo-400', text: 'text-indigo-600', bg: 'bg-indigo-50' },
  pendiente_pago: { label: 'Pend. Pago', dot: 'bg-orange-400', text: 'text-orange-600', bg: 'bg-orange-50' },
  aprobada: { label: 'Aprobada', dot: 'bg-emerald-400', text: 'text-emerald-600', bg: 'bg-emerald-50' },
  vencida: { label: 'Vencida', dot: 'bg-rose-500', text: 'text-rose-600', bg: 'bg-rose-50' },
};

function StatusBadge({ status }: { status: string }) {
  const s = statusStyle[status] || statusStyle.activa;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold whitespace-nowrap ${s.text} ${s.bg}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`}></span>
      {s.label}
    </span>
  );
}

function getScoreColor(score: number): string {
  if (score >= 80) return 'text-emerald-600';
  if (score >= 60) return 'text-amber-600';
  if (score >= 40) return 'text-orange-500';
  return 'text-red-500';
}

export default function RadiosTable({
  radios,
  onView,
  loading,
  error,
  onRetry,
  hasFilters,
}: RadiosTableProps) {
  if (loading) {
    return (
      <div className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden">
        <div className="hidden lg:block overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-background-200">
                {['Logo', 'Radio', 'Frecuencia', 'Plan', 'Estado', 'Score', 'Acciones'].map((h) => (
                  <th key={h} className="text-left px-4 py-3 text-foreground-400 text-[10px] font-bold uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 8 }).map((_, i) => (
                <tr key={i} className="border-b border-background-100 animate-pulse">
                  <td className="px-4 py-3"><div className="w-8 h-8 rounded-lg bg-background-200"></div></td>
                  <td className="px-4 py-3"><div className="w-24 h-3 bg-background-200 rounded"></div></td>
                  <td className="px-4 py-3"><div className="w-16 h-3 bg-background-200 rounded"></div></td>
                  <td className="px-4 py-3"><div className="w-12 h-4 bg-background-200 rounded-md"></div></td>
                  <td className="px-4 py-3"><div className="w-16 h-4 bg-background-200 rounded-full"></div></td>
                  <td className="px-4 py-3"><div className="w-14 h-3 bg-background-200 rounded"></div></td>
                  <td className="px-4 py-3"><div className="w-8 h-8 rounded-lg bg-background-200"></div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="lg:hidden divide-y divide-background-200">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="p-4 animate-pulse">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3"><div className="w-10 h-10 rounded-lg bg-background-200"></div><div className="space-y-2"><div className="w-32 h-3 bg-background-200 rounded"></div><div className="w-20 h-2 bg-background-200 rounded"></div></div></div>
                <div className="w-8 h-8 rounded-lg bg-background-200"></div>
              </div>
              <div className="flex gap-2"><div className="w-12 h-4 bg-background-200 rounded-md"></div><div className="w-16 h-4 bg-background-200 rounded-md"></div></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-background-50 border border-red-200 rounded-2xl p-8 flex flex-col items-center text-center gap-3">
        <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
          <i className="ri-error-warning-line text-red-500 text-xl"></i>
        </div>
        <div>
          <p className="text-foreground-800 text-sm font-semibold">Error cargando radios</p>
          <p className="text-foreground-400 text-[11px] mt-0.5">{error}</p>
        </div>
        {onRetry && (
          <button onClick={onRetry} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-50 text-red-600 text-[11px] font-bold hover:bg-red-100 cursor-pointer transition-colors whitespace-nowrap">
            <i className="ri-refresh-line text-xs"></i>Reintentar
          </button>
        )}
      </div>
    );
  }

  if (radios.length === 0) {
    const message = hasFilters ? 'No hay radios con este filtro.' : 'No hay radios registradas todavía.';
    return (
      <div className="bg-background-50 border border-background-200 rounded-2xl p-8 flex flex-col items-center text-center gap-3">
        <div className="w-12 h-12 rounded-full bg-background-100 flex items-center justify-center">
          <i className="ri-radio-line text-foreground-400 text-xl"></i>
        </div>
        <div><p className="text-foreground-800 text-sm font-semibold">Sin resultados</p><p className="text-foreground-400 text-[11px] mt-0.5">{message}</p></div>
      </div>
    );
  }

  return (
    <div className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden">
      {/* Desktop Table */}
      <div className="hidden lg:block overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-background-200">
              <th className="text-left px-4 py-3 text-foreground-400 text-[10px] font-bold uppercase tracking-wider w-12">Logo</th>
              <th className="text-left px-4 py-3 text-foreground-400 text-[10px] font-bold uppercase tracking-wider">Radio</th>
              <th className="text-left px-3 py-3 text-foreground-400 text-[10px] font-bold uppercase tracking-wider">Frecuencia</th>
              <th className="text-left px-3 py-3 text-foreground-400 text-[10px] font-bold uppercase tracking-wider">Plan</th>
              <th className="text-left px-3 py-3 text-foreground-400 text-[10px] font-bold uppercase tracking-wider">Estado</th>
              <th className="text-left px-3 py-3 text-foreground-400 text-[10px] font-bold uppercase tracking-wider">Score</th>
              <th className="text-center px-4 py-3 text-foreground-400 text-[10px] font-bold uppercase tracking-wider w-20">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {radios.map((radio, idx) => {
              const plan = planLabels[radio.plan];
              const isDimmed = radio.status === 'suspendida' || radio.status === 'cancelada' || radio.status === 'archivada';
              const scoreColor = getScoreColor(radio.radioScore.score);

              return (
                <tr
                  key={radio.id}
                  className={`border-b border-background-100 hover:bg-background-50 transition-colors cursor-pointer ${isDimmed ? 'opacity-60' : ''}`}
                  style={{ animation: `fadeIn 0.3s ease-out forwards`, animationDelay: `${idx * 25}ms`, opacity: 0 }}
                  onClick={() => onView(radio)}
                >
                  <td className="px-4 py-3">
                    <div className="w-8 h-8 rounded-lg bg-background-100 overflow-hidden shrink-0">
                      <img src={radio.logoUrl} alt={radio.name} className="w-full h-full object-cover" />
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-foreground-800 text-xs font-semibold truncate max-w-[200px] block">{radio.name}</span>
                  </td>
                  <td className="px-3 py-3">
                    <span className="text-foreground-500 text-[11px] font-mono whitespace-nowrap">{radio.frequency}</span>
                  </td>
                  <td className="px-3 py-3">
                    <span className={`px-1.5 py-0.5 rounded-md text-[9px] font-bold whitespace-nowrap ${plan.color} ${plan.bg}`}>{plan.label}</span>
                  </td>
                  <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                    <StatusBadge status={radio.status} />
                  </td>
                  <td className="px-3 py-3">
                    <span className={`text-[11px] font-bold ${scoreColor}`}>Score {radio.radioScore.score}</span>
                  </td>
                  <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => onView(radio)}
                      className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors"
                      title="Acciones"
                    >
                      <i className="ri-more-2-fill text-foreground-500 text-sm"></i>
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="lg:hidden divide-y divide-background-200">
        {radios.map((radio, idx) => {
          const plan = planLabels[radio.plan];
          const isDimmed = radio.status === 'suspendida' || radio.status === 'cancelada' || radio.status === 'archivada';
          const scoreColor = getScoreColor(radio.radioScore.score);

          return (
            <div
              key={radio.id}
              className={`p-4 hover:bg-background-50 transition-colors cursor-pointer ${isDimmed ? 'opacity-60' : ''}`}
              style={{ animation: `fadeIn 0.3s ease-out forwards`, animationDelay: `${idx * 25}ms`, opacity: 0 }}
              onClick={() => onView(radio)}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-background-100 overflow-hidden shrink-0">
                    <img src={radio.logoUrl} alt={radio.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-foreground-800 text-sm font-semibold truncate">{radio.name}</p>
                    <p className="text-foreground-400 text-[11px]">{radio.frequency}</p>
                  </div>
                </div>
                <div onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => onView(radio)}
                    className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors"
                  >
                    <i className="ri-more-2-fill text-foreground-500 text-sm"></i>
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold ${plan.color} ${plan.bg}`}>{plan.label}</span>
                <StatusBadge status={radio.status} />
                <span className={`text-[11px] font-bold ml-auto ${scoreColor}`}>Score {radio.radioScore.score}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}